<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core'
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type {
  ManagedAttachment,
  PublicEventDetails,
  PublicQuestion,
  SlotRatingInput,
  StudyGroupMember,
  UserRegistrationDetail,
} from '@/types'
import type { SlotAvailability } from '@/types/slot'

import FormField from '@/components/common/FormField.vue'
import LikertScaleInput from '@/components/questions/LikertScaleInput.vue'
import MatrixScaleInput from '@/components/questions/MatrixScaleInput.vue'
import RatingRequirementsDisplay from '@/components/registration/RatingRequirementsDisplay.vue'
import SlotAvailabilityBadge from '@/components/registration/SlotAvailabilityBadge.vue'
import SlotCategorySelectionList from '@/components/registration/SlotCategorySelectionList.vue'
import SlotDisplayName from '@/components/registration/SlotDisplayName.vue'
import SlotRatingList from '@/components/registration/SlotRatingList.vue'
import SlotSelectionList from '@/components/registration/SlotSelectionList.vue'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useDelayedLoading } from '@/composables/useDelayedLoading'
import { useEventBoostBudget } from '@/composables/useEventBoostBudget'
import { registrationStatusColor } from '@/composables/useRegistrationStatusColor'
import { eventService } from '@/services/eventService'
import userService from '@/services/userService'
import { EventQuestionType, EventType, RatingType, RegistrationStatus, Visibility } from '@/types'
import { eventUsesSlotBoosts } from '@/utils/allocationPhase'
import { getApiErrorMessage } from '@/utils/apiError'
import { toggleListValue } from '@/utils/checkboxList'
import { flattenRateableSlots, validateRatings } from '@/utils/ratingValidation'
import { buildSlotSelectionStructure, findSelectionProblem } from '@/utils/slotSelection'

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t, d, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useAppToast()

const registrationId = computed(() => Number((route.params as { id: string }).id))

const loading = ref(true)
const showLoading = useDelayedLoading(loading)
const saving = ref(false)
const error = ref<string | null>(null)
const registration = ref<UserRegistrationDetail | null>(null)
const event = ref<PublicEventDetails | null>(null)

const answers = reactive<
  Record<
    number,
    | string
    | string[]
    | boolean
    | { fileId: string; fileName: string }
    | Record<string, string | null>
    | null
  >
>({})
const fieldErrors = reactive<Record<number, string>>({})
const attachmentDeleteLoadingId = ref<string | null>(null)

// Slot selection and rating state
const selectedSlotId = ref<number | null>(null)
const selectedSlotIds = ref<number[]>([])
// Slots this registration already holds: they stay pickable after they have started.
const heldSlotIds = computed(() => [
  ...new Set([
    ...(registration.value?.selectedSlotIds ?? []),
    ...(registration.value?.assignedSlotIds ?? []),
  ]),
])
const isMultiSlotSelection = computed(() => event.value?.multipleSlotSelection === true)
const slotRatings = ref<Record<number, number>>({})
const slotBoosts = ref<Record<number, number>>({})
const slotAvailability = ref<SlotAvailability[]>([])
const ratingValidationResult = ref<{ isValid: boolean }>({ isValid: true })

const orderedQuestions = computed(() => {
  if (!event.value) {
    return [] as PublicQuestion[]
  }
  return [...event.value.questions].sort((a, b) => a.order - b.order)
})

const eventName = computed(() => {
  if (!event.value) {
    return ''
  }
  return locale.value === 'en' ? event.value.nameEN || event.value.nameDE : event.value.nameDE
})

const isAcceptedRegistration = computed(
  () => registration.value?.status === RegistrationStatus.Accepted,
)
// A questionnaire response is accepted on submission; its answers stay editable until the response
// period ends, after which the response is frozen.
const isQuestionnaire = computed(() => event.value?.eventType === EventType.Questionnaire)
const isQuestionnaireOpen = computed(() => {
  const end = event.value?.registrationEnd
  return !end || Date.now() <= new Date(end).getTime()
})
const isAppointmentRegistration = computed(() => !!registration.value?.appointmentStart)
const canSwitchAcceptedSlot = computed(() => registration.value?.canSwitchAcceptedSlot === true)
const isAcceptedSlotSwitchOnlyMode = computed(
  () =>
    isAcceptedRegistration.value && canSwitchAcceptedSlot.value && !isAppointmentRegistration.value,
)
const areRatingChangesLocked = computed(() => {
  if (!event.value?.lockRatingsAfterRegistrationEnd || !event.value.registrationEnd) {
    return false
  }

  return Date.now() >= new Date(event.value.registrationEnd).getTime()
})
const requiresSlotSelectionForEdit = computed(
  () =>
    !isAppointmentRegistration.value &&
    (isSelectionMode.value || isAcceptedSlotSwitchOnlyMode.value),
)
// Study group members share the owner's registration. They may read it, but only the owner
// answers questions, rates slots and picks the slot — the ratings belong to the registration.
const isGroupOwner = computed(() => registration.value?.isGroupOwner !== false)
const isStudyGroup = computed(() => (registration.value?.members?.length ?? 1) > 1)
const groupMembers = computed(() => registration.value?.members ?? [])
const showStudyGroupCard = computed(() => isStudyGroup.value || !!registration.value?.joinCode)

// Anyone in a group of two or more can leave. When the owner goes, the longest-standing
// remaining member takes over automatically; the last member deregisters instead.
const canLeaveGroup = computed(() => groupMembers.value.length > 1)
const successorName = computed(() => {
  if (!isGroupOwner.value) {
    return null
  }
  const successor = groupMembers.value.find((member) => !member.isOwner)
  return successor?.displayName ?? null
})
const joinShareUrl = computed(() => {
  const code = registration.value?.joinCode
  if (!code || !eventRouteId.value) {
    return null
  }
  return `${window.location.origin}/event/${eventRouteId.value}?joinCode=${code}`
})

async function copyToClipboard(value: string | null | undefined) {
  if (!value) {
    return
  }
  await navigator.clipboard.writeText(value)
  toast.add({
    color: 'success',
    title: t('common.messages.success'),
    description: t('pages.registrations.studyGroup.copied'),
    duration: 2000,
  })
}

const copyJoinCode = () => copyToClipboard(registration.value?.joinCode)
const copyJoinLink = () => copyToClipboard(joinShareUrl.value)

const memberActionLoadingId = ref<string | null>(null)
const leavingGroup = ref(false)

async function confirmRemoveMember(member: StudyGroupMember) {
  const accepted = await appConfirm({
    title: t('pages.registrations.studyGroup.removeConfirmTitle'),
    message: t('pages.registrations.studyGroup.removeConfirmMessage', {
      name: member.displayName ?? '',
    }),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('pages.registrations.studyGroup.removeMemberShort'),
    confirmColor: 'error',
  })
  if (!accepted || !registration.value) {
    return
  }

  memberActionLoadingId.value = member.appUserId
  try {
    await userService.removeStudyGroupMember(registration.value.registrationId, member.appUserId)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.registrations.studyGroup.removeSuccess'),
      duration: 3000,
    })
    await loadRegistration()
  } catch {
    toast.add({
      color: 'error',
      title: t('pages.registrations.studyGroup.removeError'),
      duration: 5000,
    })
  } finally {
    memberActionLoadingId.value = null
  }
}

async function confirmLeaveGroup() {
  const accepted = await appConfirm({
    title: t('pages.registrations.studyGroup.leaveConfirmTitle'),
    // The owner is told who takes over, since leaving hands the group to someone else rather
    // than dissolving it.
    message: successorName.value
      ? t('pages.registrations.studyGroup.leaveConfirmMessageOwner', { name: successorName.value })
      : t('pages.registrations.studyGroup.leaveConfirmMessage'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('pages.registrations.studyGroup.leave'),
    confirmColor: 'error',
  })
  if (!accepted || !registration.value) {
    return
  }

  leavingGroup.value = true
  try {
    await userService.leaveStudyGroup(registration.value.registrationId)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.registrations.studyGroup.leaveSuccess'),
      duration: 3000,
    })
    // The caller is no longer part of this registration, so the detail view would 403.
    await router.push('/registrations')
  } catch {
    toast.add({
      color: 'error',
      title: t('pages.registrations.studyGroup.leaveError'),
      duration: 5000,
    })
  } finally {
    leavingGroup.value = false
  }
}

const isEditDisabled = computed(() =>
  isQuestionnaire.value
    ? !isQuestionnaireOpen.value
    : !isGroupOwner.value ||
      isAppointmentRegistration.value ||
      (isAcceptedRegistration.value && !canSwitchAcceptedSlot.value),
)
const areNonSlotFieldsDisabled = computed(() =>
  isQuestionnaire.value
    ? !isQuestionnaireOpen.value
    : !isGroupOwner.value || isAppointmentRegistration.value || isAcceptedRegistration.value,
)
const areRatingFieldsDisabled = computed(
  () =>
    areNonSlotFieldsDisabled.value ||
    (areRatingChangesLocked.value && !isAcceptedSlotSwitchOnlyMode.value),
)
const eventRouteId = computed(() => {
  if (!registration.value) {
    return null
  }
  return registration.value.visibility === Visibility.Public
    ? registration.value.eventId.toString()
    : registration.value.privateLinkId
})

// Slot/Rating mode checks
const isSelectionMode = computed(() => {
  return !isQuestionnaire.value && event.value?.ratingType === RatingType.Selection
})

const isStarsMode = computed(() => {
  return !isQuestionnaire.value && event.value?.ratingType === RatingType.Stars
})

// Optimization events take boosts on slot ratings instead of on the registration. After the primary draw
// the secondary allocation decides.
const boostsApply = computed(() =>
  eventUsesSlotBoosts(event.value, event.value?.effectiveRatingType ?? event.value?.ratingType),
)
// The backend takes the program from the registration itself, as it does when the edit is saved.
const { budget: boostBudget } = useEventBoostBudget({
  eventId: computed(() => event.value?.id),
  productId: ref(null),
  registrationId: computed(() => registration.value?.registrationId),
  enabled: boostsApply,
})

const showSecondaryFcfsSelectionHint = computed(() => {
  if (event.value?.ratingType === RatingType.None) {
    return false
  }

  return (
    event.value?.ratingType === RatingType.Stars &&
    event.value?.effectiveRatingType === RatingType.Selection
  )
})

// Multi-select: the first rule the picks break, so the warning can say which kind it is.
const multiSlotSelectionProblem = computed(() =>
  isMultiSlotSelection.value && event.value
    ? findSelectionProblem(buildSlotSelectionStructure(event.value.slots), selectedSlotIds.value)
    : null,
)

// Slot selection validation
const isSlotSelectionValid = computed(() => {
  if (!requiresSlotSelectionForEdit.value) {
    return true
  }
  if (isMultiSlotSelection.value && event.value) {
    return multiSlotSelectionProblem.value === null
  }
  if (selectedSlotId.value === null || !event.value) {
    return false
  }

  return getRateableSlotIds(event.value).has(selectedSlotId.value)
})

// Rating validation
const isRatingValid = computed(() => {
  if (!isStarsMode.value || !event.value) {
    return true
  }
  const validation = validateRatings(slotRatings.value, event.value)
  return validation.isValid
})

const statusLabel = computed(() => {
  if (!registration.value) {
    return ''
  }
  if (isQuestionnaire.value) {
    return t('pages.registrations.questionnaire.submitted')
  }
  if (isAppointmentRegistration.value && registration.value.status === RegistrationStatus.Open) {
    return t('pages.registrations.appointmentPending')
  }
  return t(`enums.registrationStatus.${registration.value.status}`)
})

const formatAttachmentSize = (size: number) => `${(size / 1024 / 1024).toFixed(2)} MB`

async function downloadRegistrationAttachment(attachment: ManagedAttachment) {
  if (!registration.value) return

  try {
    await userService.downloadRegistrationAttachment(
      registration.value.registrationId,
      attachment.id,
      attachment.fileName,
    )
  } catch {
    toast.add({
      color: 'error',
      title: t('pages.registrations.files.downloadError'),
      duration: 4000,
    })
  }
}

async function confirmDeleteRegistrationAttachment(attachment: ManagedAttachment) {
  if (!registration.value) return

  const accepted = await appConfirm({
    title: t('pages.registrations.files.deleteTitle'),
    message: t('pages.registrations.files.deleteConfirm', { name: attachment.fileName }),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.delete'),
    confirmColor: 'error',
    confirmIcon: 'i-lucide-trash-2',
  })
  if (!accepted || !registration.value) return

  attachmentDeleteLoadingId.value = attachment.id
  try {
    await userService.deleteRegistrationAttachment(registration.value.registrationId, attachment.id)
    registration.value.attachments = registration.value.attachments.filter(
      (candidate) => candidate.id !== attachment.id,
    )
    toast.add({
      color: 'success',
      title: t('pages.registrations.files.deleteSuccess'),
      duration: 3000,
    })
  } catch {
    toast.add({
      color: 'error',
      title: t('pages.registrations.files.deleteError'),
      duration: 4000,
    })
  } finally {
    attachmentDeleteLoadingId.value = null
  }
}

function findSlotById(
  slots: PublicEventDetails['slots'],
  slotId: number,
): PublicEventDetails['slots'][number] | null {
  for (const slot of slots) {
    if (slot.id === slotId) {
      return slot
    }

    if (slot.subSlots.length > 0) {
      const nestedMatch = findSlotById(slot.subSlots, slotId)
      if (nestedMatch) {
        return nestedMatch
      }
    }
  }

  return null
}

function getRateableSlotIds(eventDetails: PublicEventDetails): Set<number> {
  return new Set(
    flattenRateableSlots(eventDetails.slots, eventDetails.ratingSubSlotMode).map((slot) => slot.id),
  )
}

const assignedSlotName = computed(() => {
  // Multi-select registrations hold several slots; name every one.
  const assignedIds = registration.value?.assignedSlotIds ?? []
  if (assignedIds.length > 1 && event.value) {
    const eventSlots = event.value.slots
    return assignedIds
      .map((id) => findSlotById(eventSlots, id)?.name)
      .filter((name): name is string => !!name)
      .join(', ')
  }

  if (!assignedSlot.value) {
    return null
  }

  return assignedSlot.value.name ?? null
})

const appointmentBookingTypeName = computed(() => {
  if (!registration.value?.appointmentBookingTypeNameDE) {
    return null
  }

  return locale.value === 'en'
    ? registration.value.appointmentBookingTypeNameEN ||
        registration.value.appointmentBookingTypeNameDE
    : registration.value.appointmentBookingTypeNameDE
})

const assignedSlot = computed(() => {
  if (!registration.value?.assignedSlotId || !event.value) {
    return null
  }

  return findSlotById(event.value.slots, registration.value.assignedSlotId)
})

function questionLabel(question: PublicQuestion, index: number) {
  return question.name || t('pages.registrations.edit.questionFallback', { number: index + 1 })
}

function getTextAnswer(questionId: number): string {
  const value = answers[questionId]
  return typeof value === 'string' ? value : ''
}

function setTextAnswer(questionId: number, value: string | undefined) {
  answers[questionId] = value ?? ''
}

function parseAnswer(
  question: PublicQuestion,
  raw: string | undefined,
  fileId?: string | null,
  fileName?: string | null,
  matrixValues?: { statement: string; value: string }[] | null,
) {
  if (question.type === EventQuestionType.Matrix) {
    const stored = new Map((matrixValues ?? []).map((entry) => [entry.statement, entry.value]))
    return Object.fromEntries(
      (question.statements ?? []).map((statement) => [statement, stored.get(statement) ?? null]),
    )
  }

  if (question.type === EventQuestionType.Upload) {
    if (fileId) {
      return { fileId, fileName: fileName ?? '' }
    }
    return null
  }

  if (!raw) {
    if (question.type === EventQuestionType.SelectMulti) {
      return [] as string[]
    }

    return ''
  }

  if (question.type === EventQuestionType.SelectMulti) {
    return raw
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  return raw
}

function initializeAnswers() {
  if (!event.value) {
    return
  }

  const answerList = registration.value?.answers ?? []
  const answerMap = new Map(answerList.map((answer) => [answer.questionId, answer]))

  orderedQuestions.value.forEach((question) => {
    const answerData = answerMap.get(question.id)
    answers[question.id] = parseAnswer(
      question,
      answerData?.answer,
      answerData?.fileId,
      answerData?.fileName,
      answerData?.matrixValues,
    )
  })
}

function initializeRatings() {
  if (!registration.value) {
    return
  }

  // Initialize slot selection from existing data
  selectedSlotId.value = registration.value.selectedSlotIds[0] ?? null
  selectedSlotIds.value = [...(registration.value.selectedSlotIds ?? [])]

  // Initialize star ratings from existing data
  const ratingsMap: Record<number, number> = {}
  const boostsMap: Record<number, number> = {}
  for (const rating of registration.value.ratings ?? []) {
    ratingsMap[rating.slotId] = rating.rating
    boostsMap[rating.slotId] = rating.boostLevel ?? 0
  }
  slotRatings.value = ratingsMap
  slotBoosts.value = boostsMap
}

function clearFieldErrors() {
  Object.keys(fieldErrors).forEach((key) => {
    delete fieldErrors[Number(key)]
  })
}

function isAnswerEmpty(
  question: PublicQuestion,
  value:
    | string
    | string[]
    | boolean
    | { fileId: string; fileName: string }
    | Record<string, string | null>
    | undefined
    | null,
) {
  // Required means every statement, so a partly-filled matrix still counts as empty.
  if (question.type === EventQuestionType.Matrix) {
    const values = (value ?? {}) as Record<string, string | null>
    return (question.statements ?? []).some((statement) => !values[statement])
  }

  if (question.type === EventQuestionType.SelectMulti) {
    return !Array.isArray(value) || value.length === 0
  }

  if (question.type === EventQuestionType.Upload) {
    return value == null
  }

  if (value == null) {
    return true
  }

  return String(value).trim().length === 0
}

function getAnswerString(
  question: PublicQuestion,
  value:
    | string
    | string[]
    | boolean
    | { fileId: string; fileName: string }
    | Record<string, string | null>
    | undefined
    | null,
) {
  // A matrix travels in its own field, never packed into the answer string.
  if (question.type === EventQuestionType.Matrix) {
    return ''
  }

  if (value == null) {
    return ''
  }

  if (question.type === EventQuestionType.SelectMulti) {
    return Array.isArray(value) ? value.join(', ') : ''
  }

  if (question.type === EventQuestionType.Upload) {
    if (typeof value === 'object' && value !== null && 'fileId' in value) {
      return (value as { fileId: string }).fileId
    }
    return ''
  }

  return String(value).trim()
}

function validateForm() {
  clearFieldErrors()

  let isValid = true

  orderedQuestions.value.forEach((question) => {
    const value = answers[question.id]

    // Required check
    if (question.isRequired && isAnswerEmpty(question, value)) {
      fieldErrors[question.id] = t('pages.registrations.edit.validation.required')
      isValid = false
      return
    }

    // Skip further validation if empty (and not required)
    if (isAnswerEmpty(question, value)) return

    // Type-specific validation
    if (question.type === EventQuestionType.Numeric) {
      const num = Number(value)
      if (isNaN(num)) {
        fieldErrors[question.id] = t('pages.registrations.edit.validation.invalidNumber')
        isValid = false
        return
      }
    }

    if (
      (question.type === EventQuestionType.SelectSingle ||
        question.type === EventQuestionType.Likert) &&
      question.options?.length &&
      !question.options.includes(String(value))
    ) {
      fieldErrors[question.id] = t('pages.registrations.edit.validation.invalidOption')
      isValid = false
      return
    }

    if (question.type === EventQuestionType.SelectMulti && Array.isArray(value)) {
      const invalidOptions = value.filter((v) => !question.options?.includes(v))
      if (question.options?.length && invalidOptions.length > 0) {
        fieldErrors[question.id] = t('pages.registrations.edit.validation.invalidOption')
        isValid = false
        return
      }
    }
  })

  return isValid
}

async function loadRegistration() {
  loading.value = true
  error.value = null

  try {
    const registrationData = await userService.getRegistration(registrationId.value)
    registration.value = registrationData

    const routeId =
      registrationData.visibility === Visibility.Public
        ? registrationData.eventId.toString()
        : registrationData.privateLinkId
    event.value = await eventService.getPublicEventDetails(routeId)
    initializeAnswers()
    initializeRatings()
  } catch (err: any) {
    console.error('Failed to load registration:', err)
    error.value = getApiErrorMessage(err, t, t('pages.registrations.edit.loadError'))
  } finally {
    loading.value = false
  }
}

async function saveRegistration() {
  if (!registration.value || !event.value) {
    return
  }

  if (isAppointmentRegistration.value || isEditDisabled.value) {
    toast.add({
      color: 'warning',
      title: t('common.messages.warning'),
      description: isAppointmentRegistration.value
        ? t('pages.registrations.edit.appointmentEditDisabled')
        : t('pages.registrations.edit.editDisabled'),
      duration: 4000,
    })
    return
  }

  if (!isAcceptedSlotSwitchOnlyMode.value && !validateForm()) {
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.registrations.edit.validation.fixErrors'),
      duration: 4000,
    })
    return
  }

  // Validate slot selection
  if (!isSlotSelectionValid.value) {
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('components.registration.slotSelection.required'),
      duration: 4000,
    })
    return
  }

  // Validate star ratings
  if (!isAcceptedSlotSwitchOnlyMode.value && !isRatingValid.value) {
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.registrations.edit.validation.fixErrors'),
      duration: 4000,
    })
    return
  }

  saving.value = true

  try {
    const request: {
      answers?: { questionId: number; answer: string }[]
      selectedSlotIds?: number[]
      ratings?: SlotRatingInput[]
    } = {}

    if (!isAcceptedSlotSwitchOnlyMode.value) {
      request.answers = orderedQuestions.value
        .map((question) => {
          if (question.type === EventQuestionType.Matrix) {
            const values = (answers[question.id] ?? {}) as Record<string, string | null>
            const matrixValues = (question.statements ?? [])
              .filter((statement) => values[statement])
              .map((statement) => ({ statement, value: values[statement]! }))

            // A matrix's answer string is always empty, so the falsy check below would drop it.
            return matrixValues.length
              ? { questionId: question.id, answer: '', matrixValues }
              : null
          }

          const answer = getAnswerString(question, answers[question.id])
          if (!answer) {
            return null
          }
          return { questionId: question.id, answer }
        })
        .filter((answer): answer is { questionId: number; answer: string } => !!answer)
    }

    if (
      requiresSlotSelectionForEdit.value &&
      (!areRatingChangesLocked.value || isAcceptedSlotSwitchOnlyMode.value)
    ) {
      if (isMultiSlotSelection.value) {
        request.selectedSlotIds = selectedSlotIds.value
      } else if (selectedSlotId.value != null) {
        request.selectedSlotIds = [selectedSlotId.value]
      }
    }

    if (!isAcceptedSlotSwitchOnlyMode.value && isStarsMode.value && !areRatingChangesLocked.value) {
      const rateableSlotIds = event.value ? getRateableSlotIds(event.value) : new Set<number>()
      request.ratings = Object.entries(slotRatings.value)
        .filter(([slotId]) => rateableSlotIds.has(parseInt(slotId)))
        .filter(([, rating]) => rating > 0)
        .map(([slotId, rating]) => ({
          slotId: parseInt(slotId),
          rating,
          boostLevel: slotBoosts.value[parseInt(slotId)] ?? 0,
        }))
    }

    await userService.updateRegistration(registration.value.registrationId, request)

    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.registrations.edit.saveSuccess'),
      duration: 4000,
    })

    router.push('/registrations')
  } catch (err: any) {
    console.error('Failed to save registration:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: getApiErrorMessage(err, t, t('pages.registrations.edit.saveError')),
      duration: 4000,
    })
  } finally {
    saving.value = false
  }
}

function goBack() {
  router.push('/registrations')
}

function goToEvent() {
  if (eventRouteId.value) {
    router.push(`/event/${eventRouteId.value}`)
  }
}

// Polling for real-time slot availability (Selection mode only)
const { pause: pausePolling, resume: resumePolling } = useIntervalFn(
  async () => {
    if (requiresSlotSelectionForEdit.value && eventRouteId.value) {
      try {
        slotAvailability.value = await eventService.getSlotAvailability(eventRouteId.value)
      } catch (err) {
        console.error('Error fetching slot availability:', err)
      }
    }
  },
  3000,
  { immediate: false },
)

// Start/stop polling based on mode
watch(requiresSlotSelectionForEdit, (requiresSlotSelection) => {
  if (requiresSlotSelection) {
    resumePolling()
  } else {
    pausePolling()
  }
})

onMounted(() => {
  loadRegistration()
})

onUnmounted(() => {
  pausePolling()
})
</script>

<template>
  <div class="p-6">
    <div v-if="showLoading" class="flex items-center justify-center py-20">
      <AppSpinner />
    </div>

    <div v-else-if="error" class="py-20 text-center">
      <UIcon
        name="i-lucide-triangle-alert"
        class="mb-4 text-5xl text-orange-700 dark:text-orange-400"
      />
      <h2 class="mb-2 text-xl font-semibold">
        {{ t('pages.registrations.edit.errorTitle') }}
      </h2>
      <p class="mb-4 text-muted">
        {{ error }}
      </p>
      <UButton
        :label="t('pages.registrations.edit.backToList')"
        icon="i-lucide-arrow-left"
        @click="goBack"
      />
    </div>

    <div v-else-if="registration && event">
      <div class="mb-6">
        <UButton
          :label="t('pages.registrations.edit.backToList')"
          icon="i-lucide-arrow-left"
          class="mb-4"
          @click="goBack"
          variant="ghost"
        />

        <div class="flex flex-col justify-between gap-4 md:flex-row md:items-start">
          <div>
            <h1 class="mb-2 text-3xl font-bold">
              {{
                isQuestionnaire
                  ? t('pages.registrations.questionnaire.title')
                  : t('pages.registrations.edit.title')
              }}
            </h1>
            <p class="text-muted">
              {{
                isQuestionnaire
                  ? t('pages.registrations.questionnaire.subtitle')
                  : t('pages.registrations.edit.subtitle')
              }}
            </p>
          </div>

          <div class="flex flex-col items-start gap-2 md:items-end">
            <UBadge :color="registrationStatusColor(registration?.status)">
              {{ statusLabel }}
            </UBadge>
            <div class="text-sm text-muted">
              {{ d(new Date(registration.createdAt), 'short') }}
            </div>
          </div>
        </div>
      </div>

      <UCard class="mb-6 border border-neutral-200 shadow-none dark:border-neutral-700">
        <template #default>
          <div class="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <div class="text-sm text-muted">
                {{ t('pages.registrations.edit.eventLabel') }}
              </div>
              <div class="text-lg font-semibold">
                {{ eventName }}
              </div>
              <div class="mt-1 flex flex-col gap-2 text-sm text-muted">
                <span v-if="!registration.appointmentStart && !isQuestionnaire">
                  {{ t('pages.registrations.edit.assignedSlotLabel') }}:
                  {{ assignedSlotName || t('pages.registrations.edit.assignedSlotNone') }}
                </span>
                <span v-if="registration.appointmentStart">
                  {{ t('pages.registrations.edit.appointmentTimeLabel') }}:
                  {{ d(new Date(registration.appointmentStart), 'datetime') }}
                  <template v-if="registration.appointmentEnd">
                    - {{ d(new Date(registration.appointmentEnd), 'timeonly') }}
                  </template>
                </span>
                <span v-if="appointmentBookingTypeName">
                  {{ t('pages.registrations.edit.appointmentTypeLabel') }}:
                  {{ appointmentBookingTypeName }}
                </span>
                <div
                  v-if="assignedSlot && !registration.appointmentStart"
                  class="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 dark:border-neutral-700"
                >
                  <div class="flex-1">
                    <SlotDisplayName :slot-item="assignedSlot" :show-description="true" />
                  </div>
                  <SlotAvailabilityBadge
                    :slot-item="assignedSlot"
                    :waitlist-enabled="!event.disableWaitlist"
                  />
                </div>
              </div>
            </div>
            <UButton
              :label="t('pages.registrations.edit.viewEvent')"
              icon="i-lucide-external-link"
              color="neutral"
              @click="goToEvent"
              variant="outline"
            />
          </div>
        </template>
      </UCard>

      <UCard
        v-if="registration.attachments.length"
        class="mb-6 border border-neutral-200 shadow-none dark:border-neutral-700"
      >
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-paperclip" aria-hidden="true" />
            {{ t('pages.registrations.files.title') }}
          </div>
        </template>
        <template #default>
          <ul role="list" class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <li
              v-for="attachment in registration.attachments"
              :key="attachment.id"
              class="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
            >
              <UIcon name="i-lucide-file" class="shrink-0 text-dimmed" aria-hidden="true" />
              <div class="min-w-0 flex-1">
                <div class="font-medium [overflow-wrap:anywhere]">
                  {{ attachment.fileName }}
                  <UIcon
                    name="i-lucide-lock"
                    v-if="attachment.isSensitive"
                    class="ml-1 inline-block text-xs text-orange-700 dark:text-orange-400"
                    :title="t('pages.registrations.files.sensitive')"
                    aria-hidden="true"
                  />
                  <span v-if="attachment.isSensitive" class="sr-only">
                    , {{ t('pages.registrations.files.sensitive') }}
                  </span>
                </div>
                <div class="text-sm text-muted">
                  {{ d(new Date(attachment.createdAt), 'dateonly') }} ·
                  {{ formatAttachmentSize(attachment.size) }}
                </div>
              </div>
              <UButton
                icon="i-lucide-download"
                color="neutral"
                :aria-label="t('pages.registrations.files.download', { name: attachment.fileName })"
                :disabled="attachmentDeleteLoadingId === attachment.id"
                @click="downloadRegistrationAttachment(attachment)"
                variant="ghost"
              />
              <UButton
                icon="i-lucide-trash"
                color="error"
                :loading="attachmentDeleteLoadingId === attachment.id"
                :aria-label="t('pages.registrations.files.delete', { name: attachment.fileName })"
                @click="confirmDeleteRegistrationAttachment(attachment)"
                variant="ghost"
              />
            </li>
          </ul>
        </template>
      </UCard>

      <UCard
        v-if="showStudyGroupCard"
        class="mb-6 border border-neutral-200 shadow-none dark:border-neutral-700"
      >
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-users" aria-hidden="true" />
            {{ t('pages.registrations.studyGroup.title') }}
          </div>
        </template>
        <template #default>
          <ul role="list" class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <li
              v-for="member in groupMembers"
              :key="member.appUserId"
              class="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div class="min-w-0 flex-1">
                <div class="font-medium [overflow-wrap:anywhere]">
                  {{ member.displayName }}
                </div>
                <div v-if="member.email" class="text-sm text-muted">{{ member.email }}</div>
              </div>
              <UBadge :color="member.isOwner ? 'primary' : 'neutral'" variant="subtle">
                {{
                  member.isOwner
                    ? t('pages.registrations.studyGroup.ownerBadge')
                    : t('pages.registrations.studyGroup.memberBadge')
                }}
              </UBadge>
              <UButton
                v-if="isGroupOwner && !member.isOwner"
                icon="i-lucide-user-minus"
                color="error"
                variant="ghost"
                :loading="memberActionLoadingId === member.appUserId"
                :aria-label="
                  t('pages.registrations.studyGroup.removeMember', { name: member.displayName })
                "
                v-app-tooltip.top="t('pages.registrations.studyGroup.removeMemberShort')"
                @click="confirmRemoveMember(member)"
              />
            </li>
          </ul>

          <div
            v-if="!isGroupOwner || canLeaveGroup"
            class="mt-4 flex items-start justify-between gap-3"
          >
            <p class="text-sm text-muted">
              {{
                isGroupOwner
                  ? t('pages.registrations.studyGroup.ownerLeaveHint')
                  : t('pages.registrations.studyGroup.memberReadOnly')
              }}
            </p>
            <UButton
              v-if="canLeaveGroup"
              icon="i-lucide-log-out"
              color="error"
              variant="outline"
              class="shrink-0"
              :loading="leavingGroup"
              :label="t('pages.registrations.studyGroup.leave')"
              @click="confirmLeaveGroup"
            />
          </div>

          <template v-if="registration.joinCode">
            <USeparator class="my-4" />
            <p class="mb-2 text-sm text-muted">
              {{
                t('pages.registrations.studyGroup.inviteDescription', {
                  remaining: registration.studyGroupSize - groupMembers.length,
                })
              }}
            </p>
            <div class="flex items-center gap-2">
              <UInput
                :model-value="registration.joinCode"
                :aria-label="t('pages.registrations.studyGroup.joinCode')"
                readonly
                class="flex-1 font-mono tracking-widest"
              />
              <UButton
                icon="i-lucide-clipboard"
                color="neutral"
                :aria-label="t('pages.registrations.studyGroup.copyCode')"
                v-app-tooltip.top="t('pages.registrations.studyGroup.copyCode')"
                @click="copyJoinCode"
                variant="outline"
              />
              <UButton
                icon="i-lucide-link"
                color="neutral"
                :aria-label="t('pages.registrations.studyGroup.copyLink')"
                v-app-tooltip.top="t('pages.registrations.studyGroup.copyLink')"
                :disabled="!joinShareUrl"
                @click="copyJoinLink"
                variant="outline"
              />
            </div>
          </template>
        </template>
      </UCard>

      <AppAlert v-if="isAcceptedSlotSwitchOnlyMode" color="info" :closable="false" class="mb-6">
        {{ t('pages.registrations.edit.acceptedSlotSwitchOnlyInfo') }}
      </AppAlert>

      <AppAlert v-else-if="isAppointmentRegistration" color="info" :closable="false" class="mb-6">
        {{ t('pages.registrations.edit.appointmentEditDisabled') }}
      </AppAlert>

      <AppAlert v-else-if="isEditDisabled" color="info" :closable="false" class="mb-6">
        {{
          isQuestionnaire
            ? t('pages.registrations.questionnaire.editClosed')
            : t('pages.registrations.edit.editDisabled')
        }}
      </AppAlert>

      <AppAlert
        v-if="!isAppointmentRegistration && showSecondaryFcfsSelectionHint"
        color="info"
        :closable="false"
        class="mb-6"
      >
        {{ t('pages.registrations.edit.secondaryFcfsEffectiveSelectionHint') }}
      </AppAlert>

      <!-- Slot Selection -->
      <UCard
        v-if="requiresSlotSelectionForEdit && event.slots.length > 0"
        class="mb-6 border border-neutral-200 shadow-none dark:border-neutral-700"
      >
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-list" />
            {{
              isMultiSlotSelection
                ? t('components.registration.slotCategorySelection.title')
                : t('components.registration.slotSelection.title')
            }}
          </div>
        </template>
        <template #default>
          <SlotCategorySelectionList
            :held-slot-ids="heldSlotIds"
            v-if="isMultiSlotSelection"
            v-model="selectedSlotIds"
            :slots="event.slots"
            :availability="slotAvailability"
            :waitlist-enabled="!event.disableWaitlist"
            :usage-level-display="event.usageLevelDisplay"
            :disabled="isEditDisabled || (areRatingChangesLocked && !isAcceptedSlotSwitchOnlyMode)"
            :require-open-capacity="isAcceptedSlotSwitchOnlyMode"
          />
          <SlotSelectionList
            :held-slot-ids="heldSlotIds"
            v-else
            v-model="selectedSlotId"
            :slots="event.slots"
            :availability="slotAvailability"
            :waitlist-enabled="!event.disableWaitlist"
            :usage-level-display="event.usageLevelDisplay"
            :disabled="isEditDisabled || (areRatingChangesLocked && !isAcceptedSlotSwitchOnlyMode)"
            :require-open-capacity="isAcceptedSlotSwitchOnlyMode"
            :sub-slot-rating-mode="event.ratingSubSlotMode"
          />
          <AppAlert
            v-if="!isSlotSelectionValid && !isEditDisabled"
            color="warning"
            :closable="false"
            class="mt-4"
          >
            {{
              isMultiSlotSelection
                ? multiSlotSelectionProblem === 'belowMinimum'
                  ? t('components.registration.slotCategorySelection.required')
                  : t('components.registration.slotCategorySelection.invalid')
                : t('components.registration.slotSelection.required')
            }}
          </AppAlert>
        </template>
      </UCard>

      <!-- Star Rating (Stars mode) -->
      <UCard
        v-if="!isAppointmentRegistration && isStarsMode && event.slots.length > 0"
        class="mb-6 border border-neutral-200 shadow-none dark:border-neutral-700"
      >
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-star" />
            {{ t('components.registration.slotRating.title') }}
          </div>
        </template>
        <template #default>
          <RatingRequirementsDisplay
            :event="event"
            :ratings="slotRatings"
            @update:valid="ratingValidationResult.isValid = $event"
          />
          <SlotRatingList
            v-model="slotRatings"
            v-model:boosts="slotBoosts"
            :boost-budget="boostBudget"
            :study-groups-enabled="event.studyGroupSize > 1"
            :boosts-locked="registration?.status !== RegistrationStatus.Open"
            :slots="event.slots"
            :disabled="areRatingFieldsDisabled"
            :sub-slot-rating-mode="event.ratingSubSlotMode"
          />
        </template>
      </UCard>

      <UCard
        v-if="!isAppointmentRegistration"
        class="border border-neutral-200 shadow-none dark:border-neutral-700"
      >
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-clipboard" />
            {{ t('pages.registrations.edit.formTitle') }}
          </div>
        </template>
        <template #default>
          <form class="space-y-6" @submit.prevent="saveRegistration">
            <div v-if="orderedQuestions.length === 0" class="text-sm text-muted">
              {{ t('pages.registrations.edit.noQuestions') }}
            </div>

            <div v-for="(question, index) in orderedQuestions" :key="question.id">
              <FormField
                v-if="
                  ![
                    EventQuestionType.SelectSingle,
                    EventQuestionType.Likert,
                    EventQuestionType.Matrix,
                  ].includes(question.type)
                "
                :label="questionLabel(question, index)"
                :for="`question-${question.id}`"
              >
                <template #default>
                  <div class="space-y-2">
                    <div class="text-xs text-muted">
                      <span v-if="question.isRequired">{{
                        t('pages.registrations.edit.required')
                      }}</span>
                      <span v-else>{{ t('pages.registrations.edit.optional') }}</span>
                    </div>

                    <UInput
                      v-if="question.type === EventQuestionType.TextSingle"
                      :id="`question-${question.id}`"
                      :model-value="getTextAnswer(question.id)"
                      @update:model-value="setTextAnswer(question.id, $event)"
                      class="w-full"
                      :class="{ 'border-error ring-1 ring-error': fieldErrors[question.id] }"
                      :disabled="areNonSlotFieldsDisabled"
                      :aria-invalid="!!fieldErrors[question.id]"
                      :aria-describedby="
                        fieldErrors[question.id] ? `question-${question.id}-error` : undefined
                      "
                    />

                    <UTextarea
                      v-else-if="question.type === EventQuestionType.TextMulti"
                      :id="`question-${question.id}`"
                      :model-value="getTextAnswer(question.id)"
                      @update:model-value="setTextAnswer(question.id, $event)"
                      autoresize
                      :rows="4"
                      class="w-full"
                      :class="{ 'border-error ring-1 ring-error': fieldErrors[question.id] }"
                      :disabled="areNonSlotFieldsDisabled"
                      :aria-invalid="!!fieldErrors[question.id]"
                      :aria-describedby="
                        fieldErrors[question.id] ? `question-${question.id}-error` : undefined
                      "
                    />

                    <UInputNumber
                      v-else-if="question.type === EventQuestionType.Numeric"
                      :id="`question-${question.id}`"
                      :model-value="
                        answers[question.id] !== '' ? Number(answers[question.id]) : null
                      "
                      @update:model-value="
                        answers[question.id] = $event != null ? String($event) : ''
                      "
                      class="w-full"
                      :class="{ 'border-error ring-1 ring-error': fieldErrors[question.id] }"
                      :disabled="areNonSlotFieldsDisabled"
                      :aria-invalid="!!fieldErrors[question.id]"
                      :aria-describedby="
                        fieldErrors[question.id] ? `question-${question.id}-error` : undefined
                      "
                      :increment="false"
                      :decrement="false"
                    />

                    <div
                      v-else-if="question.type === EventQuestionType.SelectMulti"
                      class="space-y-2"
                    >
                      <div
                        v-for="option in question.options"
                        :key="option"
                        class="flex items-center gap-2"
                      >
                        <UCheckbox
                          :model-value="((answers[question.id] as string[]) ?? []).includes(option)"
                          @update:model-value="
                            answers[question.id] = toggleListValue(
                              answers[question.id],
                              option,
                              $event,
                            )
                          "
                          :id="`question-${question.id}-${option}`"
                          :class="{ 'border-error ring-1 ring-error': fieldErrors[question.id] }"
                          :disabled="areNonSlotFieldsDisabled"
                        />
                        <label
                          :for="`question-${question.id}-${option}`"
                          class="text-sm text-neutral-700 dark:text-neutral-200"
                        >
                          {{ option }}
                        </label>
                      </div>
                    </div>

                    <FileUploadQuestion
                      v-else-if="question.type === EventQuestionType.Upload"
                      v-model="answers[question.id] as { fileId: string; fileName: string } | null"
                      :question-id="question.id"
                      :event-id="registration!.eventId"
                      :label="question.name || ''"
                      :required="question.isRequired"
                      :disabled="saving || areNonSlotFieldsDisabled"
                      :error="fieldErrors[question.id] || ''"
                    />

                    <small
                      v-if="fieldErrors[question.id]"
                      :id="`question-${question.id}-error`"
                      class="text-xs text-red-700 dark:text-red-400"
                    >
                      {{ fieldErrors[question.id] }}
                    </small>
                  </div>
                </template>
              </FormField>

              <fieldset v-else-if="question.type === EventQuestionType.Matrix" class="space-y-3">
                <legend class="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {{ questionLabel(question, index) }}
                </legend>
                <MatrixScaleInput
                  :model-value="(answers[question.id] as Record<string, string | null>) ?? {}"
                  @update:model-value="answers[question.id] = $event"
                  :statements="question.statements ?? []"
                  :options="question.options ?? []"
                  :name="`question-${question.id}`"
                  :required="question.isRequired"
                />
                <small
                  v-if="fieldErrors[question.id]"
                  class="text-xs text-red-700 dark:text-red-400"
                >
                  {{ fieldErrors[question.id] }}
                </small>
              </fieldset>

              <LikertScaleInput
                v-else-if="question.type === EventQuestionType.Likert"
                :model-value="(answers[question.id] as string | null) ?? null"
                @update:model-value="answers[question.id] = $event"
                :options="question.options ?? []"
                :label="questionLabel(question, index)"
                :name="`question-${question.id}`"
                :required="question.isRequired"
                :error="fieldErrors[question.id]"
              />

              <fieldset
                v-else
                class="space-y-3"
                :aria-describedby="
                  fieldErrors[question.id] ? `question-${question.id}-error` : undefined
                "
              >
                <legend class="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {{ questionLabel(question, index) }}
                </legend>
                <div class="text-xs text-muted">
                  <span v-if="question.isRequired">{{
                    t('pages.registrations.edit.required')
                  }}</span>
                  <span v-else>{{ t('pages.registrations.edit.optional') }}</span>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="option in question.options"
                    :key="option"
                    class="flex items-center gap-2"
                  >
                    <AppRadio
                      v-model="answers[question.id]"
                      :input-id="`question-${question.id}-${option}`"
                      :value="option"
                      :name="`question-${question.id}`"
                      :class="{ 'border-error ring-1 ring-error': fieldErrors[question.id] }"
                      :disabled="areNonSlotFieldsDisabled"
                    />
                    <label
                      :for="`question-${question.id}-${option}`"
                      class="text-sm text-neutral-700 dark:text-neutral-200"
                    >
                      {{ option }}
                    </label>
                  </div>
                </div>

                <small
                  v-if="fieldErrors[question.id]"
                  :id="`question-${question.id}-error`"
                  class="text-xs text-red-700 dark:text-red-400"
                >
                  {{ fieldErrors[question.id] }}
                </small>
              </fieldset>
            </div>

            <div class="flex flex-wrap items-center justify-between gap-3">
              <UButton
                type="button"
                color="neutral"
                :label="t('common.labels.back')"
                icon="i-lucide-arrow-left"
                @click="goBack"
                variant="ghost"
              />
              <UButton
                type="submit"
                :label="
                  saving
                    ? t('pages.registrations.edit.saving')
                    : isQuestionnaire
                      ? t('pages.registrations.questionnaire.saveAnswers')
                      : t('pages.registrations.edit.save')
                "
                icon="i-lucide-check"
                :loading="saving"
                :disabled="saving || isEditDisabled"
              />
            </div>
          </form>
        </template>
      </UCard>
    </div>
  </div>
</template>
